/**
 * SEARCH-002 — `PaletteInput` : champ cmdk + jeton de scope (partagé par les 2 shells).
 *
 * Extrait mécaniquement de `SearchPalette.tsx` (aucun changement de logique).
 */

import { type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { Command as CmdkPrimitive } from 'cmdk'
import { Search, X } from 'lucide-react'
import { CATEGORY_REGISTRY } from '@/lib/searchCategories'
import { type SearchPaletteApi } from './useSearchPalette'

// ===========================================================================
// PaletteInput — champ cmdk + jeton de scope (partagé par les 2 shells).
// ===========================================================================
export function PaletteInput({
    palette,
    inputRef,
    onFocus,
    autoFocus,
    inputClassName,
}: {
    palette: SearchPaletteApi
    inputRef?: RefObject<HTMLInputElement>
    onFocus?: () => void
    autoFocus?: boolean
    inputClassName?: string
}) {
    const { t } = useTranslation()
    const { input, setInput, scopeType, setScopeType, handleInputKeyDown } = palette
    const ScopeIcon = scopeType ? CATEGORY_REGISTRY[scopeType].icon : null
    return (
        <>
            <Search className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
            {scopeType && ScopeIcon && (
                <span className="flex shrink-0 items-center gap-1.5 rounded-md bg-primary/10 py-1 pl-2 pr-1 text-sm font-medium text-primary">
                    <ScopeIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    {t(`globalSearch.group.${CATEGORY_REGISTRY[scopeType].i18nKey}`)}
                    <button
                        type="button"
                        onClick={() => setScopeType(null)}
                        aria-label={t('globalSearch.filter.remove')}
                        className="rounded p-0.5 hover:bg-primary/20"
                    >
                        <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                </span>
            )}
            <CmdkPrimitive.Input
                ref={inputRef}
                value={input}
                onValueChange={setInput}
                onKeyDown={handleInputKeyDown}
                onFocus={onFocus}
                autoFocus={autoFocus}
                placeholder={t('globalSearch.placeholder')}
                aria-label={t('globalSearch.open_aria')}
                className={inputClassName ?? 'min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground'}
            />
        </>
    )
}
